import React, { useState } from "react";
import {
    FileText,
    Shield,
    MapPin,
    Image as ImageIcon,
    Package,
    CheckCircle,
    XCircle,
    Eye,
    EyeOff,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductSelectionModal from "../../customer/ProductSelectionModal";
import LanguageSwitcher from "../ui/LanguageSwitcher";

// ─── Country codes ────────────────────────────────────────────────────────────
const COUNTRY_CODES = [
    // ── الخليج العربي ──────────────────────
    { code: "+966", label: "🇸🇦 +966 السعودية"        },
    { code: "+971", label: "🇦🇪 +971 الإمارات"         },
    { code: "+965", label: "🇰🇼 +965 الكويت"           },
    { code: "+974", label: "🇶🇦 +974 قطر"              },
    { code: "+973", label: "🇧🇭 +973 البحرين"          },
    { code: "+968", label: "🇴🇲 +968 عُمان"            },
    // ── المشرق العربي ──────────────────────
    { code: "+20",  label: "🇪🇬 +20  مصر"              },
    { code: "+962", label: "🇯🇴 +962 الأردن"           },
    { code: "+961", label: "🇱🇧 +961 لبنان"            },
    { code: "+963", label: "🇸🇾 +963 سوريا"            },
    { code: "+964", label: "🇮🇶 +964 العراق"           },
    { code: "+970", label: "🇵🇸 +970 فلسطين"           },
    // ── شمال أفريقيا ───────────────────────
    { code: "+218", label: "🇱🇾 +218 ليبيا"            },
    { code: "+216", label: "🇹🇳 +216 تونس"             },
    { code: "+213", label: "🇩🇿 +213 الجزائر"          },
    { code: "+212", label: "🇲🇦 +212 المغرب"           },
    { code: "+249", label: "🇸🇩 +249 السودان"          },
    // ── أفريقيا العربية ────────────────────
    { code: "+967", label: "🇾🇪 +967 اليمن"            },
    { code: "+252", label: "🇸🇴 +252 الصومال"          },
    { code: "+222", label: "🇲🇷 +222 موريتانيا"        },
    { code: "+253", label: "🇩🇯 +253 جيبوتي"           },
    { code: "+269", label: "🇰🇲 +269 جزر القمر"        },
    // ── دولي ───────────────────────────────
    { code: "+1",   label: "🇺🇸 +1   الولايات المتحدة" },
    { code: "+44",  label: "🇬🇧 +44  بريطانيا"         },
    { code: "+33",  label: "🇫🇷 +33  فرنسا"            },
    { code: "+49",  label: "🇩🇪 +49  ألمانيا"          },
    { code: "+34",  label: "🇪🇸 +34  إسبانيا"          },
    { code: "+39",  label: "🇮🇹 +39  إيطاليا"          },
    { code: "+31",  label: "🇳🇱 +31  هولندا"           },
    { code: "+90",  label: "🇹🇷 +90  تركيا"            },
    { code: "+92",  label: "🇵🇰 +92  باكستان"          },
    { code: "+91",  label: "🇮🇳 +91  الهند"            },
    { code: "+86",  label: "🇨🇳 +86  الصين"            },
    { code: "+81",  label: "🇯🇵 +81  اليابان"          },
    { code: "+82",  label: "🇰🇷 +82  كوريا الجنوبية"   },
    { code: "+61",  label: "🇦🇺 +61  أستراليا"         },
    { code: "+55",  label: "🇧🇷 +55  البرازيل"         },
    { code: "+7",   label: "🇷🇺 +7   روسيا"            },
];

// ─── Component ────────────────────────────────────────────────────────────────
const SignupView = ({
    signupForm,
    setSignupForm,
    handleSignup,
    loading,
    message,
    products,
    selectedProducts,
    setSelectedProducts,
    files,
    setFiles,
    showProductSelection,
    setShowProductSelection,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [countryCode, setCountryCode] = useState("+966");
    const [localPhone, setLocalPhone] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState("");

    const handlePhoneChange = (digits) => {
        setLocalPhone(digits);
        setSignupForm({ ...signupForm, phoneNumber: countryCode + digits });
    };

    const handleCountryChange = (code) => {
        setCountryCode(code);
        setSignupForm({ ...signupForm, phoneNumber: code + localPhone });
    };

    const passwordMatch =
        confirmPassword.length > 0 && signupForm.password === confirmPassword;
    const passwordMismatch =
        confirmPassword.length > 0 && signupForm.password !== confirmPassword;

    const onSubmit = () => {
        if (passwordMismatch) return;
        handleSignup();
    };

    const documentItems = [
        { key: "commercialRegistration", label: t("auth.signup.documents.commercialRegistration"), icon: FileText },
        { key: "taxNumber",              label: t("auth.signup.documents.taxNumber"),              icon: Shield   },
        { key: "nationalAddress",        label: t("auth.signup.documents.nationalAddress"),        icon: MapPin   },
        { key: "logo",                   label: t("auth.signup.documents.logo"),                   icon: ImageIcon},
    ];

    const inputCls =
        "w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500 outline-none";

    return (
        <div className="relative min-h-screen bg-black text-white overflow-hidden">
            <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay loop muted playsInline
            >
                <source src="./login-bg.mp4" type="video/mp4" />
            </video>

            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

            <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-3xl bg-white/10 backdrop-blur-xl shadow-[0_0_50px_rgba(255,0,255,0.25)] my-6 rounded-2xl p-8 border border-white/20">
                    <div className="flex justify-end mb-4">
                        <LanguageSwitcher />
                    </div>

                    <h1 className="text-3xl font-bold text-center mb-6">
                        {t("auth.signup.title")}
                    </h1>

                    {message.text && (
                        <div className={`mb-5 p-4 rounded-xl flex items-start gap-3 border text-sm ${
                            message.type === "success"
                                ? "bg-green-500/10 border-green-400 text-green-200"
                                : message.type === "error"
                                ? "bg-red-500/10 border-red-400 text-red-200"
                                : "bg-yellow-500/10 border-yellow-400 text-yellow-200"
                        }`}>
                            {message.type === "success" ? <CheckCircle className="w-5 h-5" /> :
                             message.type === "error"   ? <XCircle className="w-5 h-5" /> :
                                                          <Shield className="w-5 h-5" />}
                            <p>{message.text}</p>
                        </div>
                    )}

                    <div className="space-y-6">
                        {/* Manager name */}
                        <div>
                            <label className="block text-sm mb-1">{t("auth.signup.managerNameLabel")}</label>
                            <input
                                type="text"
                                value={signupForm.managerName}
                                onChange={(e) => setSignupForm({ ...signupForm, managerName: e.target.value })}
                                className={inputCls}
                                placeholder={t("auth.signup.managerNamePlaceholder")}
                            />
                        </div>

                        {/* Phone + country code */}
                        <div>
                            <label className="block text-sm mb-1">{t("auth.signup.phoneLabel")}</label>
                            <div className="flex gap-2">
                                <select
                                    value={countryCode}
                                    onChange={(e) => handleCountryChange(e.target.value)}
                                    className="py-3 px-2 rounded-xl bg-white/10 border border-white/20 text-white
                                               focus:ring-2 focus:ring-pink-500 outline-none cursor-pointer"
                                    style={{ minWidth: "110px" }}
                                >
                                    {COUNTRY_CODES.map((c) => (
                                        <option key={c.code} value={c.code}
                                            className="bg-[#1a0a2e] text-white">
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="tel"
                                    value={localPhone}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                    className={`${inputCls} flex-1`}
                                />
                            </div>
                        </div>

                        {/* Company name + number */}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm mb-1">{t("auth.signup.companyNameLabel")}</label>
                                <input
                                    type="text"
                                    value={signupForm.companyName}
                                    onChange={(e) => setSignupForm({ ...signupForm, companyName: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">{t("auth.signup.companyNumberLabel")}</label>
                                <input
                                    type="text"
                                    value={signupForm.companyNumber}
                                    onChange={(e) => setSignupForm({ ...signupForm, companyNumber: e.target.value })}
                                    className={inputCls}
                                />
                            </div>

                            {/* Email */}
                            <div className="md:col-span-2">
                                <label className="block text-sm mb-1">{t("auth.signup.emailLabel")}</label>
                                <input
                                    type="email"
                                    value={signupForm.email}
                                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                                    className={inputCls}
                                />
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-sm mb-1">{t("auth.signup.passwordLabel")}</label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={signupForm.password}
                                        onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                                        className={`${inputCls} pr-11`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((p) => !p)}
                                        className="absolute inset-y-0 end-3 flex items-center text-gray-300 hover:text-white"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm password */}
                            <div>
                                <label className="block text-sm mb-1">{t("auth.signup.confirmPasswordLabel")}</label>
                                <div className="relative">
                                    <input
                                        type={showConfirm ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className={`${inputCls} pr-11 ${
                                            passwordMismatch ? "border-red-400 focus:ring-red-400" :
                                            passwordMatch    ? "border-green-400 focus:ring-green-400" : ""
                                        }`}
                                        placeholder={t("auth.signup.confirmPasswordPlaceholder")}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm((p) => !p)}
                                        className="absolute inset-y-0 end-3 flex items-center text-gray-300 hover:text-white"
                                        tabIndex={-1}
                                    >
                                        {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                                {passwordMatch && (
                                    <p className="text-green-300 text-xs mt-1 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" />
                                        {t("validation.passwordsMatch")}
                                    </p>
                                )}
                                {passwordMismatch && (
                                    <p className="text-red-300 text-xs mt-1 flex items-center gap-1">
                                        <XCircle className="w-3 h-3" />
                                        {t("validation.passwordsMismatch")}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Documents */}
                        <div className="grid md:grid-cols-2 gap-4">
                            {documentItems.map((item) => (
                                <div key={item.key}>
                                    <label className="text-sm mb-1 flex items-center gap-2">
                                        <item.icon className="w-4 h-4" />
                                        {item.label}
                                    </label>
                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => setFiles({ ...files, [item.key]: e.target.files[0] })}
                                        className="w-full py-2 px-3 rounded-xl bg-white/5 border border-white/20 text-white cursor-pointer"
                                    />
                                    {files[item.key] && (
                                        <p className="text-green-300 text-xs mt-1">
                                            {t("messages.fileSelected", { name: files[item.key].name })}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Products */}
                        <div>
                            <label className="text-sm mb-2 flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                {t("auth.signup.productsLabel")}
                            </label>
                            <button
                                type="button"
                                onClick={() => setShowProductSelection(true)}
                                className="w-full py-3 rounded-xl border border-pink-400/50 text-pink-200 bg-white/5 hover:bg-white/10 transition"
                            >
                                {selectedProducts.length > 0
                                    ? t("auth.signup.selectedProductsCount", { count: selectedProducts.length })
                                    : t("auth.signup.selectProducts")}
                            </button>
                        </div>

                        <button
                            onClick={onSubmit}
                            disabled={loading || passwordMismatch}
                            className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 transition disabled:opacity-50"
                        >
                            {loading ? t("auth.signup.loading") : t("auth.signup.submit")}
                        </button>

                        <p className="text-center text-sm mt-3 text-gray-200">
                            {t("auth.signup.haveAccount")}{" "}
                            <button className="text-pink-300 underline" onClick={() => navigate("/login")}>
                                {t("auth.signup.loginLink")}
                            </button>
                        </p>
                    </div>
                </div>

                {showProductSelection && (
                    <ProductSelectionModal
                        products={products}
                        onContinue={(selected) => {
                            setSelectedProducts(selected);
                            setShowProductSelection(false);
                        }}
                        onClose={() => setShowProductSelection(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default SignupView;
